import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";

import type { EmailForwarding } from "@/apis/gql/types";
import { Card, CardHeader } from "@/components/ui/card";
import { TypographyLarge, TypographySmall } from "@/components/ui/typography";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useEffect, useState } from "react";
import {
  EmailIntegrationCard,
  EmailIntegrationCardSkeleton,
} from "./email-integration-card";

type Props = {
  emailForwardings: EmailForwarding[];
  loading: boolean;
  onDeleteEmailForwarding: (emailForwardingId: string) => Promise<void>;
  onUpdateEmailForwarding: ({
    emailForwardingId,
    alias,
    description,
    isActive,
  }: {
    emailForwardingId: string;
    alias?: string;
    description?: string;
    isActive?: boolean;
  }) => Promise<void>;
  onCreateEmailForwarding: ({
    alias,
    description,
  }: { alias?: string; description?: string }) => Promise<void>;
};

export const ForwardingIntegrations = ({
  emailForwardings,
  loading,
  onCreateEmailForwarding,
  onDeleteEmailForwarding,
  onUpdateEmailForwarding,
}: Props) => {
  const { t } = useTranslation();
  const [createEmailFowardingLoading, setCreateEmailFowardingLoading] =
    useState(false);

  const handleCreateEmailForwarding = async () => {
    setCreateEmailFowardingLoading(true);

    try {
      await onCreateEmailForwarding({});
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setCreateEmailFowardingLoading(false);
    }
  };

  // TODO: We only have one forwarding address for now, so we can just check if any of them are active
  const isActive = emailForwardings.some(
    (emailForwarding) => emailForwarding.isActive,
  );

  const handleToggleForwarding = async (value: boolean) => {
    // TODO: We only have one forwarding address for now, so we can just update the first one
    await onUpdateEmailForwarding({
      emailForwardingId: emailForwardings[0].emailForwardingId,
      isActive: value,
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div className="flex items-start gap-4">
            <div className="space-y-2">
              <TypographyLarge>{t("forwarding.title")}</TypographyLarge>
              <TypographySmall className="text-muted-foreground">
                {t("forwarding.description")}
              </TypographySmall>
            </div>
          </div>
          {/* TODO: Add this back when we want to allow multiple forwarding addresses */}
          {/* <Button
            onClick={handleCreateEmailForwarding}
            disabled={
              createEmailFowardingLoading || emailForwardings.length >= 1
            }
          >
            {createEmailFowardingLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            {t("forwarding.create_forwarding")}
          </Button> */}
          <ActiveToggle
            isActive={isActive}
            onUpdateEmailForwarding={handleToggleForwarding}
          />
        </div>
      </CardHeader>
      <div className="flex flex-col gap-4 px-4 pb-4">
        {!isActive && (
          <Alert variant="warning">
            <AlertTitle>
              {t("forwarding.email.inactive.alert.title")}
            </AlertTitle>
            <AlertDescription>
              {t("forwarding.email.inactive.alert.description")}
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4">
            {loading ? (
              <>
                {Array.from({ length: 1 }).map((_, index) => (
                  <EmailIntegrationCardSkeleton key={index.toString()} />
                ))}
              </>
            ) : (
              emailForwardings.map((emailForwarding) => (
                <EmailIntegrationCard
                  key={emailForwarding.emailForwardingId}
                  isActive={emailForwarding.isActive}
                  emailForwardingId={emailForwarding.emailForwardingId}
                  email={emailForwarding.forwardingAddress}
                  onDeleteEmailForwarding={onDeleteEmailForwarding}
                  onUpdateEmailForwarding={onUpdateEmailForwarding}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

const ActiveToggle = ({
  isActive: _isActive,
  onUpdateEmailForwarding,
}: {
  isActive: boolean;
  onUpdateEmailForwarding: (value: boolean) => Promise<void>;
}) => {
  const [isActive, setIsActive] = useState(_isActive);

  const { t } = useTranslation();

  const handleToggleForwarding = async (value: boolean) => {
    setIsActive(value);
    try {
      await onUpdateEmailForwarding(value);
    } catch (error) {
      setIsActive(!value);
    }
  };

  useEffect(() => {
    setIsActive(_isActive);
  }, [_isActive]);

  return (
    <div className="flex items-center gap-2">
      <Label
        htmlFor="email-forwarding-toggle"
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {t("forwarding.email.toggle_forwarding_label")}
      </Label>
      <Switch
        id="email-forwarding-toggle"
        checked={isActive}
        onCheckedChange={handleToggleForwarding}
      />
    </div>
  );
};
